// MARK://dhk8/admin-shell/pages/modules/AuditPage
// Página global de auditoría (I0) — sección concentrada del registro de cambios del
// negocio. Feed del tenant (del JWT) por el grafo (platform-audit) vía clients/audit.
// FilterBar: Módulo (select) · Evento (texto → filter.eventName) · Severidad (select) ·
// Desde/Hasta (date → filter.from/to). DataTable con las MISMAS columnas que AuditTrail
// + una columna extra "Entidad" (aggregateId corto). Paginación server-side.
// Helpers de formato compartidos en components/audit-format.ts (no duplicar).
import { useState } from 'react'
import * as audit from '../../lib/clients/audit.js'
import { useAsync } from '../../lib/use-async.js'
import DataTable, { type DataTableColumn } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { auditColumns, AuditStyles } from '../../components/AuditTrail.js'
import { shortId } from '../../components/audit-format.js'

const PAGE_SIZE = 25

// Módulo (prefijo de eventName) → etiqueta ES. '' = Todos.
const MODULE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'dhk8.erp', label: 'Inventario' },
  { value: 'dhk8.catalog', label: 'Catálogo' },
  { value: 'dhk8.pos', label: 'Punto de venta' },
  { value: 'dhk8.fiscal', label: 'Fiscal' },
  { value: 'dhk8.platform', label: 'Plataforma' },
  { value: 'dhk8.crm', label: 'CRM' },
]

const SEVERITY_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Baja' },
]

export default function AuditPage() {
  const [page, setPage] = useState(1)
  const [module, setModule] = useState('')
  const [eventName, setEventName] = useState('')
  const [severity, setSeverity] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  // Resetear a la página 1 cuando cambia cualquier filtro.
  const onFilter = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1) }

  // Las fechas de los <input type="date"> son YYYY-MM-DD; las pasamos a ISO de día
  // (inicio para "desde", fin para "hasta") para que el rango sea inclusivo.
  const fromIso = from ? new Date(`${from}T00:00:00`).toISOString() : undefined
  const toIso = to ? new Date(`${to}T23:59:59`).toISOString() : undefined

  const res = useAsync(
    (s) => audit.listAuditFeed(
      { module: module || undefined, eventName: eventName || undefined, severity: severity || undefined, from: fromIso, to: toIso },
      { page, limit: PAGE_SIZE },
      s,
    ),
    [module, eventName, severity, fromIso, toIso, page],
  )

  const data = res.data
  const columns = feedColumns()

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Auditoría</h1>
          <p className="adm-page__sub">Registro de cambios del negocio</p>
        </div>
      </div>

      {res.error && (
        <div className="adm-banner adm-banner--danger" role="alert" style={{ marginBottom: 12 }}>
          No se pudo cargar el registro: {res.error}
          <button className="adm-btn adm-btn--sm" style={{ marginLeft: 8 }} onClick={res.reload}>Reintentar</button>
        </div>
      )}

      <DataTable<audit.AuditEntry>
        columns={columns}
        rows={data?.items ?? []}
        getRowKey={(e) => e.id}
        loading={res.loading}
        emptyText="Sin registros de auditoría para estos filtros."
        zebra
        page={data?.page ?? page}
        pageSize={data?.limit ?? PAGE_SIZE}
        total={data?.total ?? 0}
        onPageChange={setPage}
        toolbar={
          <FilterBar
            search={eventName}
            onSearch={onFilter(setEventName)}
            searchPlaceholder="Buscar evento… (ej. product.updated)"
            filters={[
              { key: 'module', label: 'Módulo', value: module, onChange: onFilter(setModule), options: MODULE_OPTIONS },
              { key: 'severity', label: 'Severidad', value: severity, onChange: onFilter(setSeverity), options: SEVERITY_OPTIONS },
            ]}
            actions={
              <div className="adm-audit-dates">
                <label className="adm-audit-dates__field">
                  <span className="adm-audit-dates__label">Desde</span>
                  <input type="date" className="adm-input adm-audit-dates__input" value={from} max={to || undefined} onChange={(e) => onFilter(setFrom)(e.target.value)} aria-label="Desde" />
                </label>
                <label className="adm-audit-dates__field">
                  <span className="adm-audit-dates__label">Hasta</span>
                  <input type="date" className="adm-input adm-audit-dates__input" value={to} min={from || undefined} onChange={(e) => onFilter(setTo)(e.target.value)} aria-label="Hasta" />
                </label>
              </div>
            }
          />
        }
      />

      <AuditStyles />
      <style>{`
        .adm-audit-dates { display: flex; align-items: flex-end; gap: var(--pos-space-2, 8px); }
        .adm-audit-dates__field { display: flex; flex-direction: column; gap: 4px; }
        .adm-audit-dates__label { font-size: var(--pos-text-xxs, 11px); font-weight: var(--pos-weight-medium, 500); color: var(--pos-fg-tertiary); text-transform: uppercase; letter-spacing: var(--pos-tracking-wide, 0.04em); }
        .adm-audit-dates__input { min-height: 44px; }
      `}</style>
    </div>
  )
}

// Columnas del feed = columnas del trail + "Entidad" (aggregateId corto) tras "Acción".
function feedColumns(): DataTableColumn<audit.AuditEntry>[] {
  const base = auditColumns()
  const entityCol: DataTableColumn<audit.AuditEntry> = {
    key: 'entity',
    header: 'Entidad',
    width: '120px',
    render: (e) => e.aggregateId
      ? <span className="adm-mono adm-muted">{shortId(e.aggregateId)}</span>
      : <span className="adm-muted">—</span>,
  }
  // Insertar "Entidad" justo después de "Acción" (índice 2).
  const actionIdx = base.findIndex((c) => c.key === 'action')
  const at = actionIdx >= 0 ? actionIdx + 1 : base.length
  return [...base.slice(0, at), entityCol, ...base.slice(at)]
}
